import { useState } from 'react'
import { analyticsService } from '../services/analyticsService'

interface ContactFormProps {
  isOpen: boolean
  onClose: () => void
}

type ContactType = 'feedback' | 'question' | 'request' | 'bug'

const CONTACT_TYPES: { value: ContactType; label: string; description: string }[] = [
  { value: 'feedback', label: 'Feedback', description: 'Share your thoughts' },
  { value: 'question', label: 'Question', description: 'Ask us anything' },
  { value: 'request', label: 'Access Request', description: 'Request Pro access' },
  { value: 'bug', label: 'Bug Report', description: 'Report an issue' },
]

export function ContactForm({ isOpen, onClose }: ContactFormProps) {
  const [type, setType] = useState<ContactType>('feedback')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ref = analyticsService.getRef()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          name: name.trim() || 'Anonymous',
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          ref,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to submit')
      }

      setSubmitted(true)
    } catch (err) {
      setError('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    // Reset form on close
    setType('feedback')
    setName('')
    setEmail('')
    setSubject('')
    setMessage('')
    setSubmitted(false)
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="cyber-card w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="cyber-heading text-xl">Contact Us</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {submitted ? (
          <div className="text-center py-8">
            <div className="text-green-400 text-5xl mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-display text-white mb-2">Message Sent!</h3>
            <p className="text-gray-400 mb-6">
              Thank you for reaching out. We'll get back to you as soon as possible.
            </p>
            <button onClick={handleClose} className="cyber-button">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Contact Type */}
            <div className="mb-4">
              <label className="cyber-label">What's this about?</label>
              <div className="grid grid-cols-2 gap-2">
                {CONTACT_TYPES.map((ct) => (
                  <button
                    key={ct.value}
                    type="button"
                    onClick={() => setType(ct.value)}
                    className={`py-2 px-3 rounded-lg text-sm text-left transition-all ${
                      type === ct.value
                        ? 'bg-cyber-purple/20 border border-cyber-purple text-cyber-purple'
                        : 'bg-cyber-gray text-gray-400 hover:text-white border border-transparent'
                    }`}
                  >
                    <div className="font-semibold">{ct.label}</div>
                    <div className="text-xs opacity-70">{ct.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Name */}
            <div className="mb-4">
              <label className="cyber-label">Name (optional)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="cyber-input"
              />
            </div>

            {/* Email */}
            <div className="mb-4">
              <label className="cyber-label">Email {type === 'request' && <span className="text-cyber-pink">*</span>}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="cyber-input"
                required={type === 'request'}
              />
              {type === 'request' && (
                <p className="text-xs text-gray-500 mt-1">Required for access requests so we can respond</p>
              )}
            </div>

            {/* Subject */}
            <div className="mb-4">
              <label className="cyber-label">Subject (optional)</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary"
                className="cyber-input"
              />
            </div>

            {/* Message */}
            <div className="mb-4">
              <label className="cyber-label">Message <span className="text-cyber-pink">*</span></label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  type === 'request'
                    ? "Tell us about your use case and why you need Pro access..."
                    : type === 'bug'
                    ? "Describe what happened and steps to reproduce..."
                    : "Your message..."
                }
                rows={5}
                className="cyber-input resize-none"
                required
              />
            </div>

            {/* Ref indicator */}
            {ref && (
              <p className="text-xs text-gray-500 mb-4">
                Sending as tracked user: <code className="text-cyber-blue">{ref}</code>
              </p>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="cyber-button-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!message.trim() || isSubmitting}
                className="cyber-button flex-1"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
